// 统一会话管理。真实 Supabase + fallback 到本地 mock 的双轨设计：
// - 有 VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY → 使用 Supabase Auth（魔法链接）
// - 没配置 → 继续用本地 mock session（localStorage），便于纯前端 demo / 未连后端时开发
//
// 业务代码只调用这里的 API，不直接碰 supabase.auth

import { useEffect, useState } from 'react';
import { isReviewSessionOk, parseReviewSessionResponse, reviewSessionUrl } from '@doneline/shared';
import { isDesktop } from '@/lib/desktop';
import { _setSessionActiveForGuestGate } from './guest-marker';
import { getSupabase, hasSupabaseConfig } from './supabase';

// Tauri 桌面壳里 magic link 不能落回浏览器（用户已经在 app 内）。
// 注册了 doneline:// 自定义 scheme（见 apps/desktop/src-tauri/tauri.conf.json + use-deep-link-auth.ts）。
// Supabase Dashboard → Authentication → URL Configuration → Additional Redirect URLs
// 必须把 doneline://auth/callback 加进白名单，否则发邮件这步会 422。
const DESKTOP_REDIRECT_URL = 'doneline://auth/callback';

export interface Session {
  userId: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  accessToken: string | null;
}

const MOCK_KEY = 'doneline-auth-v1';
const MOCK_USER_ID = '00000000-0000-7000-8000-000000000001';

type Listener = (s: Session | null) => void;
const listeners = new Set<Listener>();
let cached: Session | null = null;
let initialized = false;

function emit() {
  // guest 短路门的 session 腿（guest-marker 零依赖，不能反向 import 本模块）。
  // 每次会话变化都注入，保证 authed 态下 enqueue 恒不被 guest 短路。
  _setSessionActiveForGuestGate(cached != null);
  for (const l of listeners) l(cached);
}

function readMock(): Session | null {
  try {
    const raw = localStorage.getItem(MOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      email: string;
      userId?: string;
      displayName?: string | null;
      avatarUrl?: string | null;
    };
    return {
      userId: parsed.userId ?? MOCK_USER_ID,
      email: parsed.email,
      displayName: parsed.displayName ?? null,
      avatarUrl: parsed.avatarUrl ?? null,
      accessToken: null,
    };
  } catch {
    return null;
  }
}

function persistMock(s: Session) {
  localStorage.setItem(
    MOCK_KEY,
    JSON.stringify({
      email: s.email,
      userId: s.userId,
      displayName: s.displayName,
      avatarUrl: s.avatarUrl,
    }),
  );
}

function readDisplayName(metadata: Record<string, unknown> | undefined): string | null {
  if (!metadata) return null;
  const v = metadata.display_name ?? metadata.name ?? metadata.full_name;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function readAvatarUrl(metadata: Record<string, unknown> | undefined): string | null {
  if (!metadata) return null;
  const v = metadata.avatar_url ?? metadata.picture;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

async function initializeSupabase() {
  const supabase = getSupabase();

  // PKCE magic-link callback handling on the web. Why we own this here instead
  // of relying on the SDK's `detectSessionInUrl`:
  //   - That option triggers exchangeCodeForSession asynchronously after the
  //     client constructor, but `auth.getSession()` returns the in-memory
  //     cached session immediately. Result: a race where __root.tsx's
  //     beforeLoad sees `null`, redirects to /login, the URL loses `?code=`,
  //     and the SDK's pending detect work has no URL left to act on. User
  //     ends up on /login with the email link "consumed" silently.
  //   - Doing the exchange ourselves and awaiting it here makes the
  //     "session" check synchronous from the caller's POV.
  // PWA-only: Tauri's deep-link delivery doesn't touch window.location
  // (see use-deep-link-auth.ts), so this branch is skipped there.
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    if (code && !('__TAURI_INTERNALS__' in window)) {
      try {
        await supabase.auth.exchangeCodeForSession(code);
      } catch (err) {
        // Don't crash init — surface via console; user just lands logged out
        console.warn('[auth] exchangeCodeForSession failed', err);
      }
      // Strip ?code= so a refresh / share doesn't re-attempt with a used code
      url.searchParams.delete('code');
      // Some Supabase callbacks also tag along ?error=... etc.; leave those alone
      window.history.replaceState({}, '', url.toString());
    }
  }

  const { data } = await supabase.auth.getSession();
  if (data.session) {
    cached = {
      userId: data.session.user.id,
      email: data.session.user.email ?? '',
      displayName: readDisplayName(data.session.user.user_metadata),
      avatarUrl: readAvatarUrl(data.session.user.user_metadata),
      accessToken: data.session.access_token,
    };
  } else {
    cached = null;
  }
  supabase.auth.onAuthStateChange((_event, s) => {
    cached = s
      ? {
          userId: s.user.id,
          email: s.user.email ?? '',
          displayName: readDisplayName(s.user.user_metadata),
          avatarUrl: readAvatarUrl(s.user.user_metadata),
          accessToken: s.access_token,
        }
      : null;
    emit();
  });
  initialized = true;
  emit();
}

function initializeMock() {
  cached = readMock();
  initialized = true;
  emit();
}

export async function initSession(): Promise<void> {
  if (initialized) return;
  if (hasSupabaseConfig()) {
    await initializeSupabase();
  } else {
    initializeMock();
  }
}

export function getSessionSync(): Session | null {
  return cached;
}

export async function getAccessToken(): Promise<string | null> {
  if (!hasSupabaseConfig()) {
    // Mock auth + 真后端 (dev mode)：发 "dev:<uuid>" token，API dev auth 识别
    return cached ? `dev:${cached.userId}` : null;
  }
  const supabase = getSupabase();
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * 用邮件里的 6 位验证码登录（magic link 的同源备用路径；与 mobile 的
 * lib/auth/session.verifyEmailOtp 同契约）。
 *
 * 深链会在多种情况下断掉（邮件客户端剥 scheme、异设备收信、企业邮箱重写链接），
 * 输码不依赖深链。也是给审核员一条可走登录路径的前提（上架文档 §7.4e）。
 * 前提：Supabase 的 Magic Link 邮件模板要含 `{{ .Token }}`。
 */
export async function verifyEmailOtp(email: string, token: string): Promise<void> {
  if (!hasSupabaseConfig()) return; // mock 模式已直接登录，无码可验
  const supabase = getSupabase();
  const { error } = await supabase.auth.verifyOtp({
    email,
    token: token.trim(),
    type: 'email',
  });
  if (!error) return;
  if (await tryReviewSession(email, token.trim())) return;
  throw error;
}

/**
 * 审核员登录回退（上架文档 §7.4e）。
 *
 * 审核员在同一个「输入验证码」框里输我们预置的码 —— Supabase 判它无效之后，
 * 走服务端 `POST /auth/review-session` 换出真 session。UI 零改动。
 *
 * 失败时**抛回原本的 Supabase 错误**：对普通用户（真的输错了码）来说，
 * 「Token has expired or is invalid」才是有意义的提示，「审核登录失败」不是。
 */
async function tryReviewSession(email: string, code: string): Promise<boolean> {
  const base = import.meta.env.VITE_API_BASE_URL;
  if (!base) return false;
  try {
    const res = await fetch(reviewSessionUrl(base), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    if (!isReviewSessionOk(res.status)) return false;
    const tokens = parseReviewSessionResponse(await res.json());
    if (!tokens) return false;
    const { error } = await getSupabase().auth.setSession({
      access_token: tokens.accessToken,
      refresh_token: tokens.refreshToken,
    });
    return !error;
  } catch {
    // 网络失败 → 当作「这条路走不通」，让调用方抛原本的 Supabase 错误。
    // 这里静默是安全的：用户看到的仍是准确的验证码错误提示（护栏 rule K 的 (c) 情形）。
    return false;
  }
}

export async function signInWithEmail(email: string): Promise<void> {
  if (hasSupabaseConfig()) {
    const supabase = getSupabase();
    const emailRedirectTo = isDesktop()
      ? DESKTOP_REDIRECT_URL
      : typeof window !== 'undefined'
        ? `${window.location.origin}/`
        : undefined;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Tauri：doneline:// 自定义 scheme，由 use-deep-link-auth 处理回跳
        // 浏览器：落到 /，让 __root.tsx beforeLoad 根据 session 路由
        emailRedirectTo,
      },
    });
    if (error) throw error;
    return;
  }
  // Mock fallback
  const session: Session = {
    userId: MOCK_USER_ID,
    email,
    displayName: null,
    avatarUrl: null,
    accessToken: null,
  };
  persistMock(session);
  cached = session;
  emit();
}

export async function updateDisplayName(name: string): Promise<void> {
  const trimmed = name.trim();
  if (hasSupabaseConfig()) {
    const supabase = getSupabase();
    const { data, error } = await supabase.auth.updateUser({
      data: { display_name: trimmed || null },
    });
    if (error) throw error;
    if (data.user && cached) {
      cached = { ...cached, displayName: readDisplayName(data.user.user_metadata) };
      emit();
    }
    return;
  }
  // Mock fallback: persist to localStorage
  if (!cached) return;
  cached = { ...cached, displayName: trimmed || null };
  persistMock(cached);
  emit();
}

export async function updateAvatarUrl(url: string | null): Promise<void> {
  const trimmed = url?.trim() ?? '';
  const next = trimmed === '' ? null : trimmed;
  if (hasSupabaseConfig()) {
    const supabase = getSupabase();
    const { data, error } = await supabase.auth.updateUser({
      data: { avatar_url: next },
    });
    if (error) throw error;
    if (data.user && cached) {
      cached = { ...cached, avatarUrl: readAvatarUrl(data.user.user_metadata) };
      emit();
    }
    return;
  }
  if (!cached) return;
  cached = { ...cached, avatarUrl: next };
  persistMock(cached);
  emit();
}

export async function signOut(): Promise<void> {
  if (hasSupabaseConfig()) {
    await getSupabase().auth.signOut();
    return;
  }
  localStorage.removeItem(MOCK_KEY);
  cached = null;
  emit();
}

export function subscribeSession(l: Listener): () => void {
  listeners.add(l);
  if (initialized) l(cached);
  return () => listeners.delete(l);
}

// React hook
export function useSession(): Session | null {
  const [s, setS] = useState<Session | null>(cached);
  useEffect(() => {
    let active = true;
    void initSession().then(() => {
      if (active) setS(cached);
    });
    const unsub = subscribeSession((next) => {
      if (active) setS(next);
    });
    return () => {
      active = false;
      unsub();
    };
  }, []);
  return s;
}

// 给老代码兼容用的 mock session 形状（Lovable 版本有用到）
export interface MockSession {
  email: string;
  signedInAt: string;
}
export function getSession(): MockSession | null {
  if (!cached) return null;
  return { email: cached.email, signedInAt: new Date().toISOString() };
}
